import { IEdoQuestion } from "@/shared/model/edo-question.model";
import { getQuestionIcon, getQuestionType } from "@/shared/util/question-utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
} from "@/components/ui";
import { X, Check } from "lucide-react";
import { useEffect, useState } from "react";
import SelectQuestions from "./SelectQuestions";

export interface SelectQuestionsDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (questions: IEdoQuestion[]) => void;
  disabledQuestions?: IEdoQuestion[];
  selectedQuestions: IEdoQuestion[];
  setSelectedQuestions: (questions: IEdoQuestion[]) => void;
}

const SelectQuestionsDialog = (props: SelectQuestionsDialogProps) => {
  const [selectedQuestionsLocal, setSelectedQuestionsLocal] = useState<
    IEdoQuestion[]
  >(props.selectedQuestions);

  const {
    open,
    onSuccess: handleSuccess,
    onClose: handleClose,
    disabledQuestions,
    selectedQuestions,
    setSelectedQuestions,
  } = props;

  const handleSubmit = () => {
    setSelectedQuestions(selectedQuestionsLocal);
    handleSuccess(selectedQuestionsLocal);
    handleClose();
  };

  const removeQuestion = (index: number) => {
    const next = Array.from(selectedQuestionsLocal);
    next.splice(index, 1);
    setSelectedQuestionsLocal(next);
  };

  useEffect(() => {
    setSelectedQuestionsLocal(selectedQuestions);
  }, [selectedQuestions]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        showClose={false}
        className="flex max-h-[90vh] max-w-4xl flex-col gap-0 overflow-hidden p-0"
      >
        <DialogHeader className="flex shrink-0 flex-row items-center justify-between gap-4 border-b border-[var(--color-mockup-border)] px-6 py-4 dark:border-[var(--color-mockup-border)]">
          <DialogTitle>Seleziona domande dall&apos;archivio</DialogTitle>
          <div className="flex items-center gap-1">
            <IconButton
              onClick={handleClose}
              aria-label="Chiudi"
              variant="ghost"
              size="sm"
            >
              <X className="h-4 w-4" />
            </IconButton>
            <IconButton
              onClick={handleSubmit}
              aria-label="Conferma"
              variant="ghost"
              size="sm"
            >
              <Check className="h-4 w-4" />
            </IconButton>
          </div>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
          <SelectQuestions
            disabledQuestions={disabledQuestions}
            selectedQuestions={selectedQuestionsLocal}
            setSelectedQuestions={setSelectedQuestionsLocal}
          />
          <Card className="overflow-hidden rounded-2xl text-[var(--color-mockup-text-mid)]">
            <List className="divide-y divide-[var(--color-mockup-border)] p-0 dark:divide-[var(--color-mockup-border)]">
              <li className="bg-[var(--color-surface-secondary)] px-4 py-2 text-sm font-medium text-[var(--color-mockup-text-mid)] dark:bg-[var(--color-surface-secondary)] dark:text-[var(--color-mockup-text-light)]">
                Domande selezionate
              </li>
              {selectedQuestionsLocal?.length ? (
                selectedQuestionsLocal.map((question: IEdoQuestion, index: number) => (
                  <ListItem
                    key={question.id}
                    className="flex w-full items-center gap-3 px-4 py-3"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--color-surface-secondary)] dark:bg-[var(--color-surface-secondary)]">
                      {getQuestionIcon(question.type)}
                    </div>
                    <ListItemText
                      primary={question.description}
                      secondary={getQuestionType(question.type, true)}
                      className="min-w-0 flex-1 truncate"
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        onClick={() => removeQuestion(index)}
                        aria-label="Rimuovi domanda"
                        size="sm"
                        variant="ghost"
                      >
                        <X className="h-4 w-4" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))
              ) : (
                <li className="px-4 py-4 text-center text-body text-[var(--color-mockup-text-mid)]">
                  Nessuna domanda selezionata.
                </li>
              )}
            </List>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelectQuestionsDialog;
